import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';

import SubnetList from './SubnetList';
import SubnetForm from './SubnetForm';
import { jwt_decode } from '../../../utils/jwt_decode';
import baseapi from '../../../services/baseapi';


export interface SubnetData {
    id?: string;
    name: string;
    subnet: string;
    description: string;
    enabled: boolean;
    priority: number;
    excludeIPs: string[];
    ports: number[];
}

interface SubnetModalProps {
    open: boolean;
    onClose: () => void;
    agent: {
        client_id: string;
        id: string;
    };
}

type ViewMode = 'list' | 'add' | 'edit';

const SubnetModal: React.FC<SubnetModalProps> = ({ open, onClose, agent }) => {
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [subnets, setSubnets] = useState<SubnetData[]>([]);
    const [selectedSubnet, setSelectedSubnet] = useState<SubnetData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const decodedToken = jwt_decode();
    const organizationId = decodedToken.organization_id;

    // Fetch subnets when modal opens
    useEffect(() => {
        if (open) {
            fetchSubnets();
        }
    }, [open]);

    const fetchSubnets = async () => {
        setIsLoading(true);

        const params = {
            query: JSON.stringify({
                client_id: agent.client_id,
                agent_id: agent.id,
                organization_id: organizationId
            })
        }
        try {
            const response = await baseapi.get('/network_configuration', { params });

            if (response.data.status) {
                setSubnets(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch subnets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddNew = () => {
        setSelectedSubnet(null);
        setViewMode('add');
    };

    const handleEdit = (subnet: SubnetData) => {
        setSelectedSubnet(subnet);
        setViewMode('edit');
    };

    const handleDelete = async (subnetId: string) => {
        try {
            const response = await baseapi.delete(`/network_configuration/${subnetId}`);

            if (response.data.status) {
                await fetchSubnets();
            }
        } catch (error) {
            console.error('Failed to delete subnet:', error);
        }
    };

    const handleFormSuccess = async () => {
        await fetchSubnets();
        setViewMode('list');
        setSelectedSubnet(null);
    };

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedSubnet(null);
    };

    const handleClose = () => {
        setViewMode('list');
        setSelectedSubnet(null);
        onClose();
    };

    return (
        <Modal open={open} onClose={(_event, reason) => {
            // Prevent closing when clicking the backdrop
            if (reason === 'backdropClick') return;
            handleClose();
        }}>
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[700px] max-w-4xl bg-white dark:bg-gray-900/95 backdrop-blur-xl 
                border border-gray-200 dark:border-orange-400/20 shadow-2xl rounded-xl text-gray-900 dark:text-white
                relative overflow-hidden max-h-[90vh] overflow-y-auto custom-scroll">

                {/* Subtle top border */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent"></div>

                {viewMode === 'list' ? (
                    <SubnetList
                        subnets={subnets}
                        isLoading={isLoading}
                        onAddNew={handleAddNew}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onClose={handleClose}
                    />
                ) : (
                    <SubnetForm
                        agent={agent}
                        organizationId={organizationId}
                        subnet={selectedSubnet}
                        isEdit={viewMode === 'edit'}
                        onSuccess={handleFormSuccess}
                        onBack={handleBackToList}
                    />
                )}
            </div>
        </Modal>
    );
};

export default SubnetModal;
